import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

import useCurrentUser from 'dpl/common/hooks/useCurrentUser';
import LoadingDots from 'dpl/components/LoadingDots';
import {
  BUYER_APPLICATIONS_PATH,
  BUYER_MESSAGES_PATH
} from 'dpl/shared/constants/urls';
import useResourceQuery from 'dpl/shared/fetching/hooks/useResourceQuery';
import { get } from 'dpl/shared/utils/object';
import { buyerApplicationResourceDefinition } from 'dpl/shared/utils/tptConnectResourceDefinitions';
import { isNativeAppUA } from 'dpl/shared/utils';
import { ANALYTICS_VIEWS } from 'dpl/constants/analytics';

const BUTTON_VARIANTS = {
  base: 'w-100 flex items-center justify-center ph3 pv3 br3 ba',
  primary: 'fw-medium white b--blue-500 bg-blue-500',
  secondary: 'fw-normal b--moon-gray bg-light-gray',
  loading: 'bg-white b--light-purple'
};

export default function RequestInfoButton({
  breederProfileId,
  puppyId,
  variants
}) {
  const { value, isFetching } = useResourceQuery(
    buyerApplicationResourceDefinition(breederProfileId)
  );

  const currentUser = useCurrentUser();

  const initialInquiries = get(
    currentUser,
    'value.data.breeder_profiles_initial_inquiries',
    []
  );

  const initialInquiry = initialInquiries.find(
    inquiry => inquiry.breeder_profile_id === breederProfileId
  );

  const buyerAppSubmittedDate = value?.buyer_application?.submitted_at;

  const alreadyApplied = Boolean(buyerAppSubmittedDate || initialInquiry);

  function getButtonCTA() {
    return alreadyApplied ? 'View request' : 'Request info';
  }

  function handleClick() {
    if (isFetching) return;

    if (alreadyApplied) {
      window.location.href = buyerAppSubmittedDate
        ? BUYER_APPLICATIONS_PATH
        : `${BUYER_MESSAGES_PATH}/active/${initialInquiry?.conversation_id}`;
    } else {
      window.location.href = `/puppy/${puppyId}/contacts/buyer_application?view=${ANALYTICS_VIEWS.PUPPY_PROFILE}`;
    }
  }

  function getButtonClassNames() {
    const buttonVariants = { ...BUTTON_VARIANTS, ...variants };

    return classnames(buttonVariants.base, 'transition', {
      [buttonVariants.loading]: isFetching,
      [buttonVariants.primary]: !isFetching && !alreadyApplied,
      [buttonVariants.secondary]: !isFetching && alreadyApplied
    });
  }

  return (
    <>
      {!isNativeAppUA() && (
        <button
          className={getButtonClassNames()}
          onClick={handleClick}
          type="button"
          disabled={isFetching}
          data-test-id="request-info-button"
        >
          {isFetching ? (
            <LoadingDots height="1rem" isVisible />
          ) : (
            getButtonCTA()
          )}
        </button>
      )}
    </>
  );
}

RequestInfoButton.propTypes = {
  analytics: PropTypes.shape({
    view: PropTypes.string.isRequired,
    view_feature: PropTypes.string.isRequired,
    view_feature_location: PropTypes.string.isRequired
  }).isRequired,
  breederProfileId: PropTypes.number.isRequired,
  puppyId: PropTypes.number,
  variants: PropTypes.shape({
    base: PropTypes.string,
    primary: PropTypes.string,
    secondary: PropTypes.string
  })
};

RequestInfoButton.defaultProps = {
  puppyId: null,
  variants: {
    base: BUTTON_VARIANTS.base,
    primary: BUTTON_VARIANTS.primary,
    secondary: BUTTON_VARIANTS.secondary,
    loading: BUTTON_VARIANTS.loading
  }
};
